/**
 * RestoID — Yandex sign-in (standard button → Yandex ID Suggest widget).
 * ---------------------------------------------------------------------------
 * Two layers share one slot:
 *   1. a RestoID standard button — the upstream redirect flow
 *      (`useSocial().invokeSocialSignIn`, which gates terms + completes via
 *      Logto's social callback);
 *   2. the official Yandex ID Suggest widget — driven by `useYandexWidget`
 *      (token-page protocol, completes via the upstream verify/identify APIs).
 *
 * The widget fades in (opacity only) ON TOP of the static, opaque standard button
 * once it has fully rendered — no brightness dip, no ghosting. The standard
 * button's logo + label are positioned to match the widget exactly, so only the
 * account avatar visibly materializes. If the widget never mounts (SDK blocked /
 * offline), the standard button stays the working fallback.
 */
import { type ExperienceSocialConnector } from '@logto/schemas';
import { useCallback, useState } from 'react';

import useSocial from '@/containers/SocialSignInList/use-social';

import { type Lang, useCopy, useLang } from '../copy';

import { Spin } from './restoid';
import useRidTheme from './use-rid-theme';
import useYandexWidget, { YANDEX_WIDGET_PARENT_ID } from './use-yandex-widget';

const SLOT_HEIGHT = 56;

/** Yandex's two official surfaces for the "main" button, keyed by the page theme. */
const VENDOR_SKIN = {
  light: {
    rest: '#000000',
    hover: '#1a1a1a',
    ink: '#ffffff',
    spin: '#ffffff',
    track: 'rgba(255,255,255,0.22)',
  },
  dark: {
    rest: '#ffffff',
    hover: '#f2f2f2',
    ink: '#000000',
    spin: '#1a1a1a',
    track: 'rgba(0,0,0,0.18)',
  },
} as const;

// The Suggest widget renders its label in Yandex's own font (YS Text, loaded as a
// webfont with the widget CSS). Match that exact stack so the label is identical
// across the crossfade; before YS Text loads it falls back to the same Helvetica/
// Arial the widget itself uses.
const YANDEX_FONT = '"YS Text", "Helvetica Neue", Arial, sans-serif';

/* ------------------------------------------------------ vendor-fixed colors */
/* EVERY color literal in this file is Yandex's, not RestoID's, and is therefore
   deliberately OUTSIDE the `--rid-*` token system. The standard button is a
   pixel-match placeholder for the official Suggest widget that fades in on top of
   it, so the two must agree exactly or the crossfade jumps.
   https://yandex.ru/dev/id/doc/ru/codes/buttons-design — the "main" version is a
   BLACK button; Yandex inverts it to white when it sits on a dark background, and
   the SDK's `buttonTheme` selects which (see use-yandex-widget). We therefore pick
   the SAME variant the widget will render, keyed off the RestoID theme:
     page light -> #000000 / #1a1a1a surface, #ffffff ink   (the spec's main button)
     page dark  -> #ffffff / #f2f2f2 surface, #000000 ink   (its inverted twin)
   Changing the button's color beyond these two vendor variants is prohibited.
     #FC3F1D + white    the Yandex logo disc and its glyph (third-party mark)
   The only themeable thing here is the slot itself, which carries no color. */

// The two official Yandex logo glyphs, lifted verbatim from the Suggest widget's
// own assets (yaPersonalButtonLogo_ya / _yaEng) so the placeholder matches the
// widget pixel-for-pixel: ya = Cyrillic Я, yaEng = Latin Y.
const YA_GLYPH =
  'M24.7407 33.9778H29.0889V9.04443H22.7592C16.3929 9.04443 13.0538 12.303 13.0538 17.1176C13.0538 21.2731 15.2187 23.6163 19.0532 26.1609L21.3832 27.6987L18.3927 25.1907L12.4667 33.9778H17.1818L23.5115 24.5317L21.3098 23.0671C18.6496 21.2731 17.3469 19.8818 17.3469 16.8613C17.3469 14.2068 19.2183 12.4128 22.7776 12.4128H24.7223V33.9778H24.7407Z';
const YA_ENG_GLYPH =
  'M22.9515 24.2897C24.2907 27.223 24.737 28.2433 24.737 31.7665V36.4375H19.9544V28.5621L10.9313 8.9375H15.9211L22.9515 24.2897ZM28.8501 8.9375L22.9994 22.2332H27.8617L33.7284 8.9375H28.8501Z';

/** Official Yandex mark — white glyph on the brand-red disc (26px), language-matched to the widget. */
const YandexLogo = ({ lang }: { lang: Lang }) => (
  <svg width={26} height={26} viewBox="0 0 44 44" style={{ borderRadius: '50%', flexShrink: 0 }}>
    <rect width="44" height="44" fill="#FC3F1D" />
    <path d={lang === 'en' ? YA_ENG_GLYPH : YA_GLYPH} fill="white" />
  </svg>
);

const YandexSignIn = ({ connector }: { connector: ExperienceSocialConnector }) => {
  const c = useCopy();
  const lang = useLang();
  const skin = VENDOR_SKIN[useRidTheme()];
  const { invokeSocialSignIn } = useSocial();
  const { widgetReady } = useYandexWidget(connector);

  const [hover, setHover] = useState(false);
  const [loading, setLoading] = useState(false);

  // Standard button → upstream redirect flow (terms-gated). Fallback until/if the
  // widget mounts; hidden (and non-interactive) once it does.
  const onStandardClick = useCallback(async () => {
    if (loading) {
      return;
    }
    setLoading(true);
    try {
      await invokeSocialSignIn(connector);
    } finally {
      setLoading(false);
    }
  }, [loading, invokeSocialSignIn, connector]);

  return (
    <div style={{ position: 'relative', width: '100%', minHeight: SLOT_HEIGHT }}>
      {/* Yandex ID Suggest widget layer. It FADES IN (opacity only) over the static
          placeholder below — which never fades out — so there is no brightness dip
          and no ghosting, and it is only revealed once fully rendered (see
          use-yandex-widget). The placeholder's logo + label sit at the same place,
          so visually only the account avatar materializes. */}
      <div
        id={YANDEX_WIDGET_PARENT_ID}
        style={{
          position: 'absolute',
          inset: 0,
          opacity: widgetReady ? 1 : 0,
          visibility: widgetReady ? 'visible' : 'hidden',
          pointerEvents: widgetReady ? 'auto' : 'none',
          transition: 'opacity .35s ease',
          zIndex: 2,
        }}
      />
      {/* Standard RestoID button (placeholder + fallback). Stays fully opaque under
          the widget so the widget can fade in on top of it; goes non-interactive and
          a11y-hidden once the widget takes over. */}
      <button
        type="button"
        aria-hidden={widgetReady || undefined}
        tabIndex={widgetReady ? -1 : undefined}
        onClick={() => void onStandardClick()}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          width: '100%',
          height: '100%',
          padding: '0 16px',
          border: 'none',
          borderRadius: 12,
          cursor: loading ? 'default' : 'pointer',
          color: skin.ink,
          font: `500 16px/1 ${YANDEX_FONT}`,
          background: hover ? skin.hover : skin.rest,
          pointerEvents: widgetReady ? 'none' : 'auto',
          transition: 'background .15s ease',
          zIndex: 1,
        }}
      >
        <YandexLogo lang={lang} />
        <span style={{ flex: 1, textAlign: 'center' }}>{c('yandexLogin')}</span>
        {/* No avatar on the standard button — keep its 28px slot so the label stays
            centred exactly where the widget centres it; a dark spinner appears here
            only while the redirect is in flight. */}
        <span
          style={{ width: 28, height: 28, flexShrink: 0, display: 'grid', placeItems: 'center' }}
        >
          {loading ? <Spin size={20} bw={2} color={skin.spin} track={skin.track} /> : null}
        </span>
      </button>
    </div>
  );
};

export default YandexSignIn;
